import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';

/* The three states the real recorder has — RecorderBody.tsx switches on exactly
   these (it also has 'paused', which the demo never enters: a scripted pause
   would add a fourth beat to a sequence that has to land inside one tab dwell). */
export type RecState = 'idle' | 'recording' | 'done';

/* Which control the touch dot is travelling towards. Deliberately names the
   TARGET rather than a phase: the popout measures the button this names, and
   the state flip must NOT re-aim it. Pressing record swaps the idle row for the
   transport row, so a measurement keyed on `state` would yank the dot onto the
   stop button a full three seconds early. */
export type AimAt = 'record' | 'stop' | null;

/**
 * Where the touch dot can land.
 *
 * FOUR record candidates because the recorder lives in a different place at
 * each container width, and the dot presses whichever one is actually on
 * screen — see pickTarget in Recorder.tsx. Registering them all and choosing by
 * visibility keeps the choice on the same container queries that decide the
 * layout, instead of a second copy of those thresholds in JS.
 */
export interface RecorderTargets {
  /** The dock's Record row — the wide sidebar. */
  dockRecord: MutableRefObject<HTMLElement | null>;
  /** The 36px mic button — the collapsed rail. */
  railRecord: MutableRefObject<HTMLElement | null>;
  /**
   * The topbar mic button — the stacked band, where there is no sidebar at all
   * and so neither of the two above exists. Added 2026-09-06 (owner): before it,
   * this width had nothing to press, so the pop-out had to be open from the
   * start and the dot pressed a control INSIDE it. That inverted the flow the
   * other widths show — you press Record and the window appears — and left the
   * press landing on what read as an anonymous box.
   */
  topRecord: MutableRefObject<HTMLElement | null>;
  /** Kept as a last resort only; no width should reach it now. */
  popoutRecord: MutableRefObject<HTMLElement | null>;
  /** Stop is always pressed in the pop-out, as it is in the app. */
  popoutStop: MutableRefObject<HTMLElement | null>;
}

export interface RecorderDemo {
  state: RecState;
  /** Whole seconds recorded, for the 00:00:0N timer. */
  elapsed: number;
  aimAt: AimAt;
  /** True during the press beat — the dot contracts and throws a ring. */
  pressing: boolean;
  dotVisible: boolean;
  /** Reduced motion: the sequence never runs, we open on the finished state. */
  still: boolean;
  targets: RecorderTargets;
}

/* The whole script, in ms from mount, against tab 3's 9s dwell (owner,
   2026-09-06). Every number is a start time, not a duration.

   Budget: the sequence ends at 5.35s and the finished state then holds for
   ~3.6s, which is the point of the tab — a visitor who looks up mid-cycle
   should still find the Ready panel waiting rather than a blank recorder. */
const T = {
  /** Dot fades in, off to the side of its target. */
  appear: 250,
  /** ...and immediately starts travelling, so the fade and the move overlap. */
  aimRecord: 300,
  pressRecord: 1150,
  /** Press lands: idle -> recording, timer starts. */
  startRec: 1400,
  /** The finger LIFTS. Without this the dot sits parked on the panel for the
      whole three seconds — and because pressing record swaps the idle row for
      the transport row underneath it, it ends up hovering over empty space
      between two controls, reading as a stray circle rather than a touch. */
  lift: 1650,
  /** 3.0s of recording, then the finger comes back down for the stop button.
      Same instant as the travel, so it fades in already moving. */
  aimStop: 4400,
  pressStop: 5100,
  /** Press lands: recording -> done. */
  stopRec: 5350,
  hideDot: 5750,
} as const;

/** Seconds the demo "records". Must match (stopRec - startRec) / 1000. */
export const DEMO_DURATION_S = 3;

/** 00:00:03 — the app's own zero-padded H:MM:SS (RecorderBody.tsx:8-13). */
export function formatRecTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/**
 * Runs the scripted record -> stop -> ready sequence once per mount.
 *
 * Mount IS the trigger: tab 3's view is only rendered while that tab is active
 * (Hero mounts one tab at a time), so the sequence restarts every time a visitor
 * arrives on the tab and never runs while they are looking at something else.
 */
export function useRecorderDemo(): RecorderDemo {
  /* Read once, at mount, rather than subscribing: the sequence is scheduled in
     one shot below and a mid-flight preference change has nothing to re-drive.
     Guarded for SSR — this hook runs inside the prerender. */
  const still = useRef(
    typeof window !== 'undefined' &&
      Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
  ).current;

  const [state, setState] = useState<RecState>(still ? 'done' : 'idle');
  const [elapsed, setElapsed] = useState(still ? DEMO_DURATION_S : 0);
  const [aimAt, setAimAt] = useState<AimAt>(null);
  const [pressing, setPressing] = useState(false);
  const [dotVisible, setDotVisible] = useState(false);

  const targets: RecorderTargets = {
    dockRecord: useRef<HTMLElement | null>(null),
    railRecord: useRef<HTMLElement | null>(null),
    topRecord: useRef<HTMLElement | null>(null),
    popoutRecord: useRef<HTMLElement | null>(null),
    popoutStop: useRef<HTMLElement | null>(null),
  };

  useEffect(() => {
    if (still) return;

    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(fn, ms));
    };
    /* The press beat is 250ms of contraction; every press is scheduled through
       here so the two cannot drift apart. */
    const press = (ms: number) => {
      at(ms, () => setPressing(true));
      at(ms + 250, () => setPressing(false));
    };

    at(T.appear, () => setDotVisible(true));
    at(T.aimRecord, () => setAimAt('record'));
    press(T.pressRecord);
    at(T.startRec, () => setState('recording'));
    /* Tick the timer on the same schedule as the transport, not on an interval:
       an interval started here would be one tick out of phase with the state
       flip, and the panel would show 00:00:00 for a beat after RECORDING lit. */
    for (let s = 1; s <= DEMO_DURATION_S; s += 1) {
      at(T.startRec + s * 1000, () => setElapsed(s));
    }
    at(T.lift, () => setDotVisible(false));
    at(T.aimStop, () => {
      setDotVisible(true);
      setAimAt('stop');
    });
    press(T.pressStop);
    at(T.stopRec, () => setState('done'));
    at(T.hideDot, () => {
      setDotVisible(false);
      setAimAt(null);
    });

    return () => timers.forEach(window.clearTimeout);
  }, [still]);

  /* `targets` is intentionally OUT of the dep list: it is a stable bag of refs
     created once above, and including it would only add a value that never
     changes identity anyway. */
  return useMemo(
    () => ({ state, elapsed, aimAt, pressing, dotVisible, still, targets }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, elapsed, aimAt, pressing, dotVisible, still]
  );
}

/* Context, because the sequence drives TWO surfaces at once — the dock in the
   sidebar and the floating pop-out — and they are the same recording session in
   the real app. Two independent hook calls would be two timers racing to agree
   on one timer readout. */
const RecorderDemoContext = createContext<RecorderDemo | null>(null);

export const RecorderDemoProvider = RecorderDemoContext.Provider;

/** Null outside the provider, so the recorder surfaces can no-op on other tabs. */
export function useRecorderDemoState(): RecorderDemo | null {
  return useContext(RecorderDemoContext);
}
